import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { theme, DatePicker, Row, Tooltip, Col } from 'antd';
import { AppDispatch, RootState } from 'store/configureStore';
import TitleBar from 'components/TitleBar';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import ReportModal from 'components/Modal/AdditionalModal';
import { getTableDataAdditional } from 'constants/excelData';
import useFilters from 'utils/hooks/useFilters';
import { globalOp } from 'store/ducks/Global';
import {
  set_selectedDate,
  set_selectedICTService,
  set_selectedFunction,
  set_selectedICTSystemByFunction,
  set_selectedICTSystembyICTService,
  set_financialEntityLeiId,
} from 'store/ducks/Reportings/reportingSlice';
import { contractual_informationOp } from 'store/ducks/DataEntry/ContractualInformation';
import { ict_serviceOp } from 'store/ducks/DataEntry/ICTServices';
import { functionOp } from 'store/ducks/DataEntry/Functions';
import { entityOp } from 'store/ducks/DataEntry/Entities';
import { SystemOption } from 'store/ducks/Reportings/types';
import { ReportingHoverStyle } from 'components/Tables/Reporting/Reporting.styled';
import * as Styled from '../Layout.styled';
import { useTranslation } from 'react-i18next';

const { useToken } = theme;

const ReportingLayout: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { token } = useToken();
  const { t } = useTranslation();
  const { scheme } = useSelector((state: RootState) => state.global);
  useSelector((state: RootState) => state.reportings);
  const { financial_entity_lei_id } = useSelector(
    (state: RootState) => state.reportings,
  );
  const tableDataAdditional = getTableDataAdditional(t);

  const [ictData, setICTData] = useState<any[]>([]);
  const [entityMaintaining, setEntityMaintaining] = useState<any[]>([]);
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const [selectedFunction, setSelectedFunction] = useState<number | null>(null);
  const [selectedICTSystemByFunction, setSelectedICTSystemByFunction] =
    useState<number | null>(null);
  const [selectedICTSystemByICTServices, setSelectedICTSystemByICTServices] =
    useState<number | null>(null);
  const [systemsForFunctions, setSystemsForFunctions] = useState<
    SystemOption[]
  >([]);
  const [systemsForICTServices, setSystemsForICTServices] = useState<
    SystemOption[]
  >([]);
  const location = useLocation();
  const queryParams = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });
  const [functionData, setFunctionData] = useState<any[]>([]);
  const [selectedEntity, setSelectedEntity] = useState(
    queryParams.entity || null,
  );
  const [selectedEntityName, setSelectedEntityName] = useState();
  const { query, handleFilterChangeForReportings, extractedOptions } =
    useFilters();

  const fetchEntityMaintaining = async () => {
    try {
      const response = await entityOp.fetchAllFinancialEntities();
      const data = response?.data;
      if (data.length > 0) {
        const updatedTPPData = data.map((item: any, index: number) => ({
          key: index,
          id: item.id,
          financial_entity_name: item.financial_entity_name,
        }));
        setEntityMaintaining(updatedTPPData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSelectICTChange = (value: number) => {
    setSelectedService(value);
    dispatch(set_selectedICTService(value));
  };

  const handleSelectFunctionChange = (value: number) => {
    setSelectedFunction(value);
    dispatch(set_selectedFunction(value));
  };

  const handleSelectICTSystemByFunctionChange = (value: number) => {
    setSelectedICTSystemByFunction(value);
    dispatch(set_selectedICTSystemByFunction(value));
  };

  const handleSelectICTSystemByICTServicesChange = (value: number) => {
    setSelectedICTSystemByICTServices(value);
    dispatch(set_selectedICTSystembyICTService(value));
  };

  const onChangeStartDate = (
    date: dayjs.Dayjs | null,
    dateString: string | string[],
  ) => {
    if (typeof dateString === 'string') {
      dispatch(set_selectedDate(dateString));
    }
  };

  useEffect(() => {
    dispatch(globalOp.setFilters());
  }, [dispatch]);

  useEffect(() => {
    if (selectedEntity) {
      fetchICTServicesByEntity();
      fetchFunctionsByEntity();
      fetchICTSystemsCategoryFunction(selectedEntity, query.entity);
      fetchICTSystemsCategoryICTServices(selectedEntity, query.entity);
    }
  }, [selectedEntity, query]);

  useEffect(() => {
    fetchEntityMaintaining();
  }, []);

  const fetchICTServicesByEntity = async () => {
    try {
      const response =
        await contractual_informationOp.fetchICTServicesbyEntityName({
          entity_id: selectedEntity,
          entity_name: selectedEntityName,
        });
      const data = response?.data;
      if (data.length > 0) {
        const updatedICTServicedData = data.map((item: any, index: number) => ({
          key: index,
          id: item.id,
          ict_services_name: item.ict_services_name,
          description: item.description,
        }));
        setICTData(updatedICTServicedData);
      } else {
        setICTData([]);
      }
    } catch (error) {
      console.log(error, 'error');
    }
  };

  const fetchFunctionsByEntity = async () => {
    try {
      const response = await ict_serviceOp.fetchFunctionsbyEntityName({
        entity_id: selectedEntity,
        entity_name: selectedEntityName,
      });
      const data = response?.data;
      if (data.length > 0) {
        const updatedFunctionsData = data.map((item: any, index: number) => ({
          key: index,
          id: item.id,
          function_name: item.function_name,
        }));
        setFunctionData(updatedFunctionsData);
      } else {
        setFunctionData([]);
      }
    } catch (error) {
      console.log(error, 'error');
    }
  };

  const handleEntityChange = (
    value: any,
    option?: { value: any; label: any; key: any },
  ) => {
    if (!value || !option) {
      setSelectedEntity(null);
      setSelectedService(null);
      return;
    }

    setSelectedEntity(option.value);
    setSelectedEntityName(option.label);
    handleFilterChangeForReportings(option.value, option.label);
    setSelectedService(null);
  };

  const options = entityMaintaining.map((option) => ({
    value: Number(option.id),
    key: option.key,
    label: option.financial_entity_name,
  }));

  const fetchICTSystemsCategoryFunction = async (
    entityId: any,
    entityName: any,
  ) => {
    try {
      const response =
        await functionOp.fetchICTSystemsCategoryFunctionbyEntityName({
          entity_id: entityId,
          entity_name: entityName,
        });
      if (response?.data) {
        const formattedOptions: SystemOption[] = response.data.map(
          (system: { name: string; id: number }) => ({
            label: system.name,
            value: system.id,
            key: system.id,
          }),
        );
        setSystemsForFunctions(formattedOptions);
      }
    } catch (error) {
      console.log('Error fetching ICT systems:', error);
    }
  };

  const fetchICTSystemsCategoryICTServices = async (
    entityId: any,
    entityName: any,
  ) => {
    try {
      const response =
        await ict_serviceOp.fetchICTSystemsCategoryICTServicesbyEntityName({
          entity_id: entityId,
          entity_name: entityName,
        });
      if (response?.data) {
        const formattedOptions: SystemOption[] = response.data.map(
          (system: { name: string; id: number }) => ({
            label: system.name,
            value: system.id,
            key: system.id,
          }),
        );
        setSystemsForICTServices(formattedOptions);
      }
    } catch (error) {
      console.log('Error fetching ICT systems:', error);
    }
  };

  const handleChangeFinancialEntity = (selectedOption: any) => {
    dispatch(set_financialEntityLeiId(Number(selectedOption)));
  };

  return (
    <Styled.StyledLayout token={token}>
      {(scheme === 'firstTheme' || scheme === 'eighthTheme') && (
        <ReportingHoverStyle />
      )}

      <TitleBar title={t('ADDITIONAL_REPORTING.TITLE')} />

      <Styled.FilterCard theme={scheme}>
        <Styled.FilterTitle theme={scheme}>
          {t('ADDITIONAL_REPORTING.REPORTING_PARAMETERS')}
        </Styled.FilterTitle>

        <Styled.FilterGrid>
          {/* Entity */}
          <Styled.FilterItem>
            <Styled.FilterLabel theme={scheme}>
              {t('ADDITIONAL_REPORTING.SELECT_ENTITY_REPORTED')}
            </Styled.FilterLabel>

            <Styled.SelectFilter
              placeholder={t('ADDITIONAL_REPORTING.SELECT_ENTITY_PLACEHOLDER')}
              allowClear
              showSearch
              options={extractedOptions}
              value={queryParams.entity}
              onChange={handleEntityChange}
              filterOption={(input: string, option: { label: string }) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Styled.FilterItem>

          {/* Reference Date */}
          <Styled.FilterItem>
            <Styled.FilterLabel theme={scheme}>
              {t('ADDITIONAL_REPORTING.SELECT_REPORTING_DATE')}
            </Styled.FilterLabel>

            <Styled.SmallDatePicker>
              <DatePicker onChange={onChangeStartDate} />
            </Styled.SmallDatePicker>
          </Styled.FilterItem>

          {/* ICT Service */}
          <Styled.FilterItem>
            <Styled.FilterLabel theme={scheme}>
              {t('ADDITIONAL_REPORTING.SELECT_ICT_SERVICE')}
            </Styled.FilterLabel>

            <Styled.SelectFilter
              allowClear
              showSearch
              options={ictData.map((item) => ({
                value: item.id,
                label: item.description ?? String(item.id),
                key: item.key,
              }))}
              value={selectedService ?? undefined}
              onChange={handleSelectICTChange}
              disabled={!selectedEntity}
              filterOption={(input: string, option: { label: string }) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Styled.FilterItem>

          {/* Function */}
          <Styled.FilterItem>
            <Styled.FilterLabel theme={scheme}>
              {t('ADDITIONAL_REPORTING.SELECT_FUNCTION')}
            </Styled.FilterLabel>

            <Styled.SelectFilter
              allowClear
              showSearch
              options={functionData.map((item) => ({
                value: item.id,
                label: item.function_name,
                key: item.key,
              }))}
              value={selectedFunction ?? undefined}
              onChange={handleSelectFunctionChange}
              disabled={!selectedEntity}
              filterOption={(input: string, option: { label: string }) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Styled.FilterItem>

          {/* ICT System by Function */}
          <Styled.FilterItem>
            <Styled.FilterLabel theme={scheme}>
              {t('ADDITIONAL_REPORTING.ICT_SYSTEM_BY_FUNCTION')}
            </Styled.FilterLabel>

            <Styled.SelectFilter
              allowClear
              options={systemsForFunctions}
              value={
                systemsForFunctions.find(
                  (opt) => opt.value === selectedICTSystemByFunction,
                ) ?? undefined
              }
              onChange={handleSelectICTSystemByFunctionChange}
              disabled={!selectedEntity}
              filterOption={(input: string, option: { label: string }) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Styled.FilterItem>

          {/* ICT System by ICT Service */}
          <Styled.FilterItem>
            <Styled.FilterLabel theme={scheme}>
              {t('ADDITIONAL_REPORTING.ICT_SYSTEM_BY_ICT_SERVICE')}
            </Styled.FilterLabel>

            <Styled.SelectFilter
              allowClear
              options={systemsForICTServices}
              value={
                systemsForICTServices.find(
                  (opt) => opt.value === selectedICTSystemByICTServices,
                ) ?? undefined
              }
              onChange={handleSelectICTSystemByICTServicesChange}
              disabled={!selectedEntity}
              filterOption={(input: string, option: { label: string }) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Styled.FilterItem>

          {/* Entity by Financial LEI */}
          <Styled.FilterItem>
            <Styled.FilterLabel theme={scheme}>
              {t('ADDITIONAL_REPORTING.SELECT_ENTITY_BY_LEI')}
            </Styled.FilterLabel>

            <Styled.SelectFilter
              allowClear
              options={options}
              value={
                options.find(
                  (o) => o.value === Number(financial_entity_lei_id),
                ) || null
              }
              onChange={handleChangeFinancialEntity}
              filterOption={(input: string, option: { label: string }) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Styled.FilterItem>
        </Styled.FilterGrid>
      </Styled.FilterCard>

      <Styled.HorizontalLayout theme={scheme}>
        <Styled.GridContainer token={token}>
          <Styled.Container>
            <Row>
              <Col>
                <Styled.SectionTitle token={token}>
                  {t('ADDITIONAL_REPORTING.ADDITIONAL_REPORTS')}
                </Styled.SectionTitle>

                <Styled.Section section="Reports" token={token}>
                  <Styled.List>
                    {tableDataAdditional.map((item) => (
                      <Tooltip
                        key={item.title}
                        title={t(item.description)}
                        placement="right"
                      >
                        <Styled.CardLine theme={scheme}>
                          <div>
                            <ReportModal
                              title={t(item.title)}
                              // tableNumber={t(item.title)}
                              tableNumber={item.title}
                              headers={[]}
                            />
                          </div>
                        </Styled.CardLine>
                      </Tooltip>
                    ))}
                  </Styled.List>
                </Styled.Section>
              </Col>
            </Row>
          </Styled.Container>
        </Styled.GridContainer>
      </Styled.HorizontalLayout>
    </Styled.StyledLayout>
  );
};

export default ReportingLayout;
